import { createSelector } from '@reduxjs/toolkit';
import axios, { AxiosInstance, AxiosRequestConfig, ResponseType } from 'axios';
import { useCallback, useMemo } from 'react';

import { getFileName } from '../../util';
import { useAppDispatch, useAppSelector } from '../hooks';
import { AppThunk, RootState } from '../store';
import { AppError, MetaInfo } from '../types';
import { useAxios } from './axios';
import { fetchFailed, fetchReset, fetchStart, fetchSuccess } from './reducer';

type HttpMethod = 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH';
interface SubstitutionObject {
  [key: string]: string | number | undefined | null;
}
interface UseAPIData<T> {
  data?: T;
  requesting?: boolean;
  error?: AppError;
  statusCode?: number;
  meta?: MetaInfo;
}
type DoRequest = (
  body?: any,
  params?: any,
  subtitleForce?: SubstitutionObject,
  keyForce?: string
) => void;
type ClearState = () => void;
export type UseAPI<T = any> = [UseAPIData<T>, DoRequest, ClearState];

const apiSelector = (state: RootState) => state.api;
const createDataSelector = (key: string) =>
  createSelector(apiSelector, (api) => api[key] || {});

/**
 * Helper function to substitute parameters in URL strings
 * @param str - The string containing parameters (e.g., '/users/{id}')
 * @param params - Object with substitution values
 * @returns String with substituted values
 */
const substituteParams = (str: string, params?: SubstitutionObject) =>
  params !== undefined
    ? Object.keys(params).reduce(
        (current, key) => current.replace(`{${key}}`, `${params[key] ?? ''}`),
        str
      )
    : str;

/**
 * Core API client class that handles request lifecycle
 */
class APIClient {
  private url: string;
  private key: string;
  private method: HttpMethod;
  private responseType: ResponseType;

  constructor(
    url: string,
    key: string,
    method: HttpMethod,
    responseType: ResponseType
  ) {
    this.url = url;
    this.key = `${key}:${method}`; // Unique key for request tracking
    this.method = method;
    this.responseType = responseType;
  }

  getKey() {
    return this.key;
  }

  /**
   * Aborts any ongoing request for this client
   * @param state Current Redux state
   */
  abort(state: RootState) {
    const { abortController } = state.api[this.key] || {};
    abortController?.abort();
  }

  /**
   * Creates thunk action to reset this request's state
   */
  resetState(): AppThunk {
    return (dispatch, getState) => {
      this.abort(getState());
      dispatch(fetchReset({ key: this.key }));
    };
  }

  /**
   * Creates thunk action to execute the API request
   * @param axiosInstance Configured axios instance
   * @param options Additional request options
   * @param substitleForce Force parameter substitutions
   * @param keyForce Override request tracking key
   */
  request(
    axiosInstance?: AxiosInstance,
    options?: AxiosRequestConfig,
    substitleForce?: SubstitutionObject,
    keyForce?: string
  ): AppThunk {
    return async (dispatch, getState) => {
      if (!this.url || !axiosInstance) return;

      // Cancel any existing request
      this.abort(getState());

      const abortController = new AbortController();
      dispatch(
        fetchStart({
          key: keyForce ?? this.key,
          abortController,
        })
      );

      try {
        // Substitute URL parameters
        const urlF = substitleForce
          ? substituteParams(this.url, substitleForce)
          : this.url;
        const url = import.meta.env.VITE_API_URL + urlF;

        // Execute request
        const {
          data,
          status: statusCode,
          headers,
        } = await axiosInstance({
          url,
          method: this.method,
          signal: abortController.signal,
          responseType: this.responseType,
          ...options,
        });

        // Handle response data
        let customMeta = {};
        let successData = data?.data || data?.items;

        // Special handling for blob responses (file downloads)
        if (this.responseType === 'blob') {
          const contentDisposition = headers['content-disposition'];
          const fileName = getFileName(contentDisposition);
          customMeta = { fileName };
          successData = data;
        }

        dispatch(
          fetchSuccess({
            key: this.key,
            data: successData,
            meta: {
              ...data?.meta,
              ...customMeta,
            },
            statusCode,
          })
        );
      } catch (e) {
        // Error handling
        const internalError: AppError = { message: 'Internal Server Error' };
        const busyError: AppError = {
          message: 'The system is busy, please try again later',
        };

        if (axios.isCancel(e)) {
          // Request was cancelled - no action needed
        } else if (axios.isAxiosError(e)) {
          const { data, status: statusCode } = e.response ?? {};
          let error = data?.error || internalError;

          // Special handling for gateway errors
          if (statusCode === 502 || statusCode === 504) {
            error = busyError;
          }
          dispatch(fetchFailed({ key: this.key, error, statusCode }));
        } else {
          console.error(e);
          dispatch(fetchFailed({ key: this.key, error: internalError }));
        }
      }
    };
  }
}

interface UseAPIOptions {
  responseType?: ResponseType;
  key?: string;
}

/**
 * Primary hook for making API requests
 * @param url API endpoint URL (may contain {param} placeholders)
 * @param substitutions Values for URL placeholders
 * @param method HTTP method
 * @param options Additional options
 * @returns Tuple containing [data, request function, reset function]
 */
export const useAPI = <T = any>(
  url: string,
  substitutions: SubstitutionObject = {},
  method: HttpMethod = 'GET',
  { responseType = 'json', key = url }: UseAPIOptions = {}
): UseAPI<T> => {
  const dispatch = useAppDispatch();
  const substitutedUrl = substituteParams(url, substitutions);
  const substitutedKey = substituteParams(key, substitutions);
  const axiosInstance = useAxios();

  // Memoize client instance
  const client: APIClient = useMemo(
    () => new APIClient(substitutedUrl, substitutedKey, method, responseType),
    [substitutedUrl, substitutedKey, method, responseType]
  );

  // Request function
  const doRequest = useCallback<DoRequest>(
    (data, params, substitleForce, keyForce) => {
      return dispatch(
        client.request(
          axiosInstance.current,
          { data, params },
          substitleForce,
          keyForce
        )
      );
    },
    [axiosInstance, client, dispatch]
  );

  // State selector
  const dataKey = client.getKey();
  const dataSelector = useMemo(() => createDataSelector(dataKey), [dataKey]);
  const data = useAppSelector((state) => dataSelector(state));

  // Reset function
  const resetStateAPI = useCallback(
    () => dispatch(client.resetState()),
    [client, dispatch]
  );

  return [data, doRequest, resetStateAPI];
};

/**
 * Cached version of useAPI that only fetches if data isn't already loaded
 * @param url API endpoint URL
 * @param substitutions URL parameters
 * @param method HTTP method
 * @param options Additional options
 * @returns Same signature as useAPI but with smart fetching
 */
export const useCachedAPI = <T = any>(
  url: string,
  substitutions: SubstitutionObject = {},
  method: HttpMethod = 'GET',
  options: UseAPIOptions = {}
): UseAPI<T> => {
  const [data, fetchData, clearData] = useAPI(
    url,
    substitutions,
    method,
    options
  );

  // Wrapper that implements caching behavior
  const fetchDataWrapper = useCallback(
    (force?: boolean) => {
      if (force || data.requesting === undefined) {
        fetchData();
      }
    },
    [data.requesting, fetchData]
  );

  return [data, fetchDataWrapper, clearData];
};
